import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, Plus } from "lucide-react";
import { AddItemControl, type AddItemLevel, type AddItemRun } from "@/components/AddItemControl";

interface AddItemButtonProps {
  topics: { id: string; name: string }[];
  total: number;
  cap: number;
  busy: boolean;
  generatingTopicId?: string | null;
  /** Live phase + elapsed time for the in-flight generation, if any. */
  run?: AddItemRun | null;
  onAdd: (args: { topicId: string; level: AddItemLevel; mode: "ai" | "blank" }) => void;
  className?: string;
  size?: "sm" | "default";
}

// Compact "Add item" button that opens the add-item controls in a dialog —
// used where there isn't room for the inline control (the mobile panel).
export function AddItemButton({ topics, total, cap, busy, generatingTopicId, run, onAdd, className = "", size = "sm" }: AddItemButtonProps) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="secondary"
          size={size}
          className={className}
          disabled={busy}
          data-testid="button-add-item-open"
        >
          {busy ? (
            <>
              Generating…
              <Loader2 className="w-4 h-4 animate-spin" />
            </>
          ) : (
            <>
              Add item
              <Plus className="w-4 h-4" />
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add item</DialogTitle>
        </DialogHeader>
        <AddItemControl
          topics={topics}
          total={total}
          cap={cap}
          busy={busy}
          generatingTopicId={generatingTopicId}
          run={run}
          hideHeading
          onAdd={(args) => {
            onAdd(args);
            setOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
